import { Component, OnInit, signal, inject } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { TagComponent } from '../../../../shared/components/tag/tag.component';
import { RideStatusType } from '../../../../shared/enums/rideStatusType';
import { rideStopCargoStatus } from '../../../../shared/enums/rideStopCargoStatus';
import { RequestBuilderService } from '../../../../shared/services/request-builder.service';
import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import moment from 'moment';

@Component({
  selector: 'app-change-passenger-status-dialog',
  standalone: true,
  imports: [FormsModule, ButtonComponent, AutoCompleteModule, TagComponent, SelectModule, DatePickerModule],
  templateUrl: './change-passenger-status-dialog.component.html',
  styleUrl: './change-passenger-status-dialog.component.scss',
})
export class ChangePassengerStatusDialogComponent implements OnInit {
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private readonly requestBuilder = inject(RequestBuilderService);

  loading = false;
  reason: string = '';
  suggestions: string[] = [];
  filteredSuggestions: string[] = [];
  statusOptions = signal<{ name: string; id: number }[]>([]);
  arrivalTime: Date | null = null;
  arrivalTimeError: string | null = null;

  ngOnInit() {
    console.log(this.config.data);

    // Set arrival time if it exists
    if (this.config.data.stop?.actualArrivalTimeUtc && this.config.data.stop.actualArrivalTimeUtc.length > 0 && this.config.data.stop.actualArrivalTimeUtc != '00:00:00') {
      this.setArrivalTimeValue();
    } else if (this.config.data.stop?.scheduled_arrival_time) {
      // Auto-populate with scheduled arrival time
      this.setScheduledArrivalTime();
    }

    this.setOptions();
  }

  setArrivalTimeValue() {
    // Parse UTC time string format "13:43:01.0940000" and convert to ride's local timezone
    const timeString = this.config.data.stop.actualArrivalTimeUtc;
    const timezoneOffsetInSeconds = this.config.data.timezoneOffsetInSeconds ?? 0;
    const today = new Date();

    // Calculate the time offset in hours
    const timeOffset = timezoneOffsetInSeconds / 60 / 60;

    // Calculate the string offset for moment.js zone()
    const strOffset = timeOffset < 0 ? `-0${timeOffset * -1}:00` : `0${timeOffset * -1}:00`;

    // Get date string in YYYY-MM-DD format
    const dateOfService = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');

    // Use moment to convert UTC time to ride's local timezone
    const localTimeString = moment
      .utc((dateOfService + ' ' + timeString).replaceAll('-', '/'))
      .zone(strOffset)
      .format('YYYY-MM-DD HH:mm:ss');

    // Parse the local time string into a Date object
    this.arrivalTime = new Date(localTimeString);
  }

  setScheduledArrivalTime() {
    const scheduledTime = this.config.data.stop.scheduled_arrival_time;
    if (!scheduledTime) return;

    this.arrivalTime = this.parseTimeStringToDate(scheduledTime);
  }

  filterSuggestions(event: any, autoComplete: AutoComplete) {
    if (this.reason) {
      this.filteredSuggestions = this.suggestions.filter(suggestion => suggestion.toLowerCase().includes(this.reason.toLowerCase()));
    } else {
      this.filteredSuggestions = [...this.suggestions];
      if (this.filteredSuggestions.length > 0) {
        autoComplete.show();
      }
    }
  }

  private parseTimeStringToDate(timeString: string): Date | null {
    if (!timeString) return null;

    const timeParts = timeString.split(':');
    if (timeParts.length < 2) return null;

    const hours = Number.parseInt(timeParts[0], 10);
    const minutes = Number.parseInt(timeParts[1], 10);
    const seconds = timeParts.length > 2 ? Number.parseInt(timeParts[2], 10) : 0;

    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes, seconds, 0);
  }

  validateArrivalTime(selectedTime: Date | null): boolean {
    if (!selectedTime) {
      this.arrivalTimeError = null;
      return true;
    }

    // Validation 1: Check if arrival time is before ride start time (first stop)
    const firstStopScheduledTime = this.config.data.firstStopScheduledTime;
    if (firstStopScheduledTime) {
      const rideStartTime = this.parseTimeStringToDate(firstStopScheduledTime);
      if (rideStartTime && selectedTime.getTime() < rideStartTime.getTime()) {
        this.arrivalTimeError = 'Arrival time cannot be before ride start time';
        return false;
      }
    }

    // Validation 2: Check if arrival time is more than 4 hours after scheduled arrival time
    const scheduledArrivalTime = this.config.data.stop?.scheduled_arrival_time;
    if (scheduledArrivalTime) {
      const scheduledTime = this.parseTimeStringToDate(scheduledArrivalTime);
      if (scheduledTime) {
        // Calculate 4 hours after scheduled time (in milliseconds)
        const fourHoursInMs = 4 * 60 * 60 * 1000;
        const maxAllowedTime = new Date(scheduledTime.getTime() + fourHoursInMs);

        if (selectedTime.getTime() > maxAllowedTime.getTime()) {
          this.arrivalTimeError = 'Arrival time cannot be more than 4 hours after scheduled arrival time';
          return false;
        }
      }
    }

    this.arrivalTimeError = null;
    return true;
  }

  onArrivalTimeChange(selectedTime: Date | null) {
    this.validateArrivalTime(selectedTime);
  }

  setOptions() {
    const passenger = this.config.data.passenger;
    const isPickUp = passenger?.actionType?.toLowerCase() === 'pick-up';

    if (isPickUp && passenger?.statusId === RideStatusType.None) {
      this.statusOptions.update(prev => [...prev, { name: 'Pick-up', id: rideStopCargoStatus.PickedUp }, { name: 'No-show', id: RideStatusType.NoShow }]);
    } else if (isPickUp && passenger?.statusId === rideStopCargoStatus.PickedUp) {
      this.statusOptions.update(prev => [...prev, { name: 'No-show', id: RideStatusType.NoShow }]);
    } else if (isPickUp && passenger?.statusId === RideStatusType.NoShow) {
      this.statusOptions.update(prev => [...prev, { name: 'Pick-up', id: rideStopCargoStatus.PickedUp }]);
    } else if (!isPickUp && passenger?.statusId === RideStatusType.None) {
      this.statusOptions.update(prev => [...prev, { name: 'Dropped-off', id: rideStopCargoStatus.DroppedOff }, { name: 'Not Dropped-off', id: rideStopCargoStatus.NotDroppedOff }]);
    } else if (!isPickUp && passenger?.statusId === rideStopCargoStatus.DroppedOff) {
      this.statusOptions.update(prev => [...prev, { name: 'Not Dropped-off', id: rideStopCargoStatus.NotDroppedOff }]);
    } else if (!isPickUp && passenger?.statusId === rideStopCargoStatus.NotDroppedOff) {
      this.statusOptions.update(prev => [...prev, { name: 'Dropped-off', id: rideStopCargoStatus.DroppedOff }]);
    }
  }

  private formatTimeForAPI(date: Date): string {
    // The API expects the time in the ride's local timezone
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    const milliseconds = date.getMilliseconds();

    // Get today's date
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');

    // Format as ISO string in ride's local timezone (without timezone offset)
    return `${year}-${month}-${day}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(milliseconds).padStart(3, '0')}Z`;
  }

  confirmAction(form: NgForm) {
    if (form?.invalid) return;

    // Validate arrival time before submitting
    const selectedDate: Date = form.form.value.arrivalTime;
    if (!this.validateArrivalTime(selectedDate)) {
      return;
    }

    this.loading = true;

    const body: any = {
      rideStatusId: form.form.value.status.id,
      internalNote: form.form.value.reason,
      isNotifyPartner: true,
      isChangeTransportationStatus: true,
      isNotifyFamilyAppUser: true,
    };

    // The API expects the time in the ride's local timezone
    body.actualArrivalTime = this.formatTimeForAPI(selectedDate);

    console.log(body);

    this.requestBuilder.patch(`stop-cargoes/${this.config.data.passenger.stopCargoId}/status`, body).subscribe({
      next: () => {
        this.loading = false;
        this.ref.close({ confirmed: true });
      },
      error: error => {
        this.loading = false;
      },
    });
  }
}
